import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes } from 'react';
import * as DM from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * DropdownMenu primitive (Compose API over Radix UI).
 *
 * Mirrors the shadcn-style API but with FavelaFood dark theme tokens applied
 * via Tailwind. Supports nested submenus, checkbox/radio items and labels.
 */
export const DropdownMenu = DM.Root;
export const DropdownMenuTrigger = DM.Trigger;
export const DropdownMenuGroup = DM.Group;
export const DropdownMenuPortal = DM.Portal;
export const DropdownMenuSub = DM.Sub;
export const DropdownMenuRadioGroup = DM.RadioGroup;

const itemBase =
  'relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm ' +
  'text-white outline-none transition-colors data-[highlighted]:bg-bg-hover ' +
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50';

export const DropdownMenuSubTrigger = forwardRef<
  ElementRef<typeof DM.SubTrigger>,
  ComponentPropsWithoutRef<typeof DM.SubTrigger> & { inset?: boolean }
>(function DropdownMenuSubTrigger({ className, inset, children, ...props }, ref) {
  return (
    <DM.SubTrigger
      ref={ref}
      className={cn(itemBase, inset && 'pl-8', className)}
      {...props}
    >
      {children}
      <ChevronRight size={14} className="ml-auto" aria-hidden />
    </DM.SubTrigger>
  );
});

export const DropdownMenuSubContent = forwardRef<
  ElementRef<typeof DM.SubContent>,
  ComponentPropsWithoutRef<typeof DM.SubContent>
>(function DropdownMenuSubContent({ className, ...props }, ref) {
  return (
    <DM.SubContent
      ref={ref}
      className={cn(
        'z-50 min-w-[10rem] overflow-hidden rounded-card border border-border bg-bg-card p-1 ' +
          'text-white shadow-2xl animate-fade-in',
        className,
      )}
      {...props}
    />
  );
});

export const DropdownMenuContent = forwardRef<
  ElementRef<typeof DM.Content>,
  ComponentPropsWithoutRef<typeof DM.Content>
>(function DropdownMenuContent({ className, sideOffset = 6, ...props }, ref) {
  return (
    <DM.Portal>
      <DM.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-50 min-w-[10rem] overflow-hidden rounded-card border border-border bg-bg-card p-1 ' +
            'text-white shadow-2xl animate-fade-in',
          className,
        )}
        {...props}
      />
    </DM.Portal>
  );
});

export const DropdownMenuItem = forwardRef<
  ElementRef<typeof DM.Item>,
  ComponentPropsWithoutRef<typeof DM.Item> & { inset?: boolean }
>(function DropdownMenuItem({ className, inset, ...props }, ref) {
  return <DM.Item ref={ref} className={cn(itemBase, inset && 'pl-8', className)} {...props} />;
});

export const DropdownMenuCheckboxItem = forwardRef<
  ElementRef<typeof DM.CheckboxItem>,
  ComponentPropsWithoutRef<typeof DM.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <DM.CheckboxItem
      ref={ref}
      className={cn(itemBase, 'pl-8', className)}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DM.ItemIndicator>
          <Check size={14} aria-hidden />
        </DM.ItemIndicator>
      </span>
      {children}
    </DM.CheckboxItem>
  );
});

export const DropdownMenuRadioItem = forwardRef<
  ElementRef<typeof DM.RadioItem>,
  ComponentPropsWithoutRef<typeof DM.RadioItem>
>(function DropdownMenuRadioItem({ className, children, ...props }, ref) {
  return (
    <DM.RadioItem ref={ref} className={cn(itemBase, 'pl-8', className)} {...props}>
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DM.ItemIndicator>
          <Circle size={8} className="fill-current" aria-hidden />
        </DM.ItemIndicator>
      </span>
      {children}
    </DM.RadioItem>
  );
});

export const DropdownMenuLabel = forwardRef<
  ElementRef<typeof DM.Label>,
  ComponentPropsWithoutRef<typeof DM.Label> & { inset?: boolean }
>(function DropdownMenuLabel({ className, inset, ...props }, ref) {
  return (
    <DM.Label
      ref={ref}
      className={cn('px-2 py-1.5 text-xs font-medium text-muted', inset && 'pl-8', className)}
      {...props}
    />
  );
});

export const DropdownMenuSeparator = forwardRef<
  ElementRef<typeof DM.Separator>,
  ComponentPropsWithoutRef<typeof DM.Separator>
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return <DM.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />;
});

export function DropdownMenuShortcut({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn('ml-auto text-xs tracking-widest text-muted', className)} {...props} />;
}
